import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import { CAPABILITY_GROUPS } from "@/lib/brands";
import { ArrowUpRight } from "lucide-react";
import CapabilityFieldClose from "@/components/CapabilityFieldClose";
import EMark from "@/components/EMark";

export default function Capabilities() {
  return (
    <div data-testid="capabilities-page">
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: T.bg }}>
        <div aria-hidden className="absolute inset-0 opacity-25 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at center, ${T.border}66 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent 82%)",
        }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20">
          <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.22em] uppercase" style={{ color: T.muted }}>
            <span className="inline-flex items-center gap-2"><EMark /> · CAPABILITIES · MMXX</span>
            <span className="flex-1 h-px" style={{ background: T.border }} />
            <span style={{ color: T.accent }}>05 capability groups</span>
          </div>
          <span className="block mt-10 font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.accent }}>
            CAPABILITIES
          </span>
          <h1 className="font-fraunces font-light text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-[-0.015em] mt-6 max-w-4xl" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
            What <span className="italic" style={{ color: T.accent }}>Erenown</span> enables.
          </h1>
          <p className="mt-8 max-w-2xl font-intertight text-[16px] leading-[1.9]" style={{ color: T.textSoft }}>
            Erenown brings together launch support, operating structures, institutional systems,
            trade-oriented pathways, and digital evolution under one central direction. These capabilities are
            designed to work as a coherent whole — not as disconnected services.
          </p>
        </div>
      </section>

      {/* GROUPS */}
      <section className="border-t" style={{ borderColor: T.border, background: T.surface }} data-testid="capabilities-groups">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 gap-px" style={{ background: T.border }}>
            {CAPABILITY_GROUPS.map((g, i) => (
              <div
                key={g.id}
                data-testid={`capability-group-${g.id}`}
                className="p-8 md:p-12 grid grid-cols-12 gap-8 md:gap-10"
                style={{ background: T.surface2 }}
              >
                <div className="col-span-12 md:col-span-3">
                  <div className="flex items-center gap-3 font-plexmono text-[10.5px] tracking-[0.24em] uppercase" style={{ color: T.muted }}>
                    <span style={{ color: T.accent }}>0{i + 1}</span>
                    <span className="flex-1 h-px" style={{ background: T.border }} />
                  </div>
                  <h3 className="font-fraunces font-normal text-2xl md:text-3xl mt-5 leading-[1.18]" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
                    {g.label}
                  </h3>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <p className="font-intertight text-[15.5px] leading-[1.9]" style={{ color: T.textSoft }}>
                    {g.body}
                  </p>
                </div>

                <div className="col-span-12 md:col-span-3 md:text-right">
                  <div className="font-plexmono text-[10px] tracking-[0.24em] uppercase mb-3" style={{ color: T.muted }}>
                    Linked lines
                  </div>
                  <ul className="space-y-2">
                    {g.lines.map((name) => {
                      const id = name.toLowerCase();
                      return (
                        <li key={name} data-testid={`capability-line-${g.id}-${id}`}>
                          <Link
                            to={`/orbit?brand=${id}`}
                            className="group inline-flex items-center gap-2 font-fraunces text-lg md:text-xl transition-colors"
                            style={{ color: T.text }}
                          >
                            <span style={{ color: T.warm }}>·</span>
                            <span className="group-hover:underline" style={{ textUnderlineOffset: 4 }}>{name}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING — Capability Field (distinct from Orbit's seal & Arrival's constellation) */}
      <CapabilityFieldClose />
    </div>
  );
}
